import {
  MemberIntakeCaseInfo,
  MemberIntakePhq4Answer,
  ReferralIntake,
} from "../../domain/Referral/schema"
import { MemberIntakeContent } from "../../domain/CMS/schema"

export const MEMBER_INTAKE_OTHER_ISSUE = "other"

export type MemberIntakePresentingProblemPart = {
  display: string
  otherText?: string | null
}

export type MemberIntakePresentingProblemDisplay = {
  primary: MemberIntakePresentingProblemPart
  secondary: MemberIntakePresentingProblemPart
  combinedText: string
}

export const isM3MemberIntakeFromPacket = (
  memberIntake?: MemberIntakeCaseInfo | null,
): boolean => memberIntake?.hasM3Data === true

const hasM3IntakeSnapshotOnReferral = (
  referralIntake: ReferralIntake,
): boolean =>
  [
    referralIntake.memberIntakeWhoNeedsCare,
    referralIntake.memberIntakePrimaryIssue,
    referralIntake.memberIntakeSecondaryIssue,
    referralIntake.memberIntakeStateCode,
    referralIntake.memberIntakeZipCode,
  ].some((value) => !!value?.trim())

/**
 * M3 display is driven by hasM3Data when the API sets it (19985+).
 * When hasM3Data is unset, infer M3 from persisted snapshot fields (partial local seeds).
 */
export const isM3ReferralIntake = (
  referralIntake?: ReferralIntake | null,
): boolean => {
  if (!referralIntake) return false
  if (referralIntake.hasM3Data === true) return true
  if (referralIntake.hasM3Data === false) return false
  return hasM3IntakeSnapshotOnReferral(referralIntake)
}

const isOtherIssue = (issue?: string | null): boolean =>
  issue?.trim().toLowerCase() === MEMBER_INTAKE_OTHER_ISSUE

export const buildPresentingProblemPart = (
  issue?: string | null,
  otherText?: string | null,
): MemberIntakePresentingProblemPart => {
  const trimmedIssue = issue?.trim() ?? ""
  if (!trimmedIssue) {
    return { display: "" }
  }
  if (isOtherIssue(trimmedIssue)) {
    return { display: "Other", otherText: otherText?.trim() || null }
  }
  return { display: trimmedIssue }
}

export const buildPresentingProblemDisplay = (
  primaryIssue?: string | null,
  primaryIssueOtherText?: string | null,
  secondaryIssue?: string | null,
  secondaryIssueOtherText?: string | null,
): MemberIntakePresentingProblemDisplay => {
  const primary = buildPresentingProblemPart(
    primaryIssue,
    primaryIssueOtherText,
  )
  const secondary = buildPresentingProblemPart(
    secondaryIssue,
    secondaryIssueOtherText,
  )
  const combinedText = [primary.display, secondary.display]
    .filter((part) => part.length > 0)
    .join(" ; ")

  return { primary, secondary, combinedText }
}

export const formatM3AttendeesFromPacket = (
  memberIntake?: MemberIntakeCaseInfo | null,
): string => memberIntake?.whoNeedsCare?.trim() ?? ""

export const formatM3AttendeesFromIntake = (
  referralIntake?: ReferralIntake | null,
): string => referralIntake?.memberIntakeWhoNeedsCare?.trim() ?? ""

export const formatM3LocationFromIntake = (
  referralIntake?: ReferralIntake | null,
): string => {
  const state = referralIntake?.memberIntakeStateCode?.trim()
  const zip = referralIntake?.memberIntakeZipCode?.trim()
  if (state && zip) return `${state}, ${zip}`
  return state || zip || ""
}

export const buildPresentingProblemFromPacket = (
  memberIntake?: MemberIntakeCaseInfo | null,
): MemberIntakePresentingProblemDisplay =>
  buildPresentingProblemDisplay(
    memberIntake?.primaryIssue,
    memberIntake?.primaryIssueOtherText,
    memberIntake?.secondaryIssue,
    memberIntake?.secondaryIssueOtherText,
  )

export const buildPresentingProblemFromIntake = (
  referralIntake?: ReferralIntake | null,
): MemberIntakePresentingProblemDisplay =>
  buildPresentingProblemDisplay(
    referralIntake?.memberIntakePrimaryIssue,
    referralIntake?.memberIntakePrimaryIssueOtherText,
    referralIntake?.memberIntakeSecondaryIssue,
    referralIntake?.memberIntakeSecondaryIssueOtherText,
  )

/** member-service questionDisplayId → member_intake CMS key */
export const PHQ4_QUESTION_DISPLAY_ID_TO_CMS_KEY: Record<string, string> = {
  "phq4-nervous": "phq4QuestionNervous",
  "phq4-worry": "phq4QuestionWorrying",
  "phq4-depressed": "phq4QuestionDepressed",
  "phq4-unmotivated": "phq4QuestionLittleInterest",
  "phq4-1": "phq4QuestionNervous",
  "phq4-2": "phq4QuestionWorrying",
  "phq4-3": "phq4QuestionDepressed",
  "phq4-4": "phq4QuestionLittleInterest",
}

/** Hardcoded fallbacks when CMS is unavailable; keyed by API questionDisplayId. */
export const PHQ4_QUESTION_LABELS: Record<string, string> = {
  "phq4-nervous": "Feeling nervous, anxious or on edge?",
  "phq4-worry": "Not being able to stop or control worrying?",
  "phq4-depressed": "Feeling down, depressed or hopeless?",
  "phq4-unmotivated": "Little interest or pleasure in doing things?",
  "phq4-1": "Feeling nervous, anxious or on edge?",
  "phq4-2": "Not being able to stop or control worrying?",
  "phq4-3": "Feeling down, depressed or hopeless?",
  "phq4-4": "Little interest or pleasure in doing things?",
}

export const getPhq4QuestionLabel = (
  questionDisplayId: string,
  memberIntakeContent?: MemberIntakeContent | null,
): string => {
  const cmsKey =
    PHQ4_QUESTION_DISPLAY_ID_TO_CMS_KEY[questionDisplayId] ?? questionDisplayId
  const cmsLabel =
    memberIntakeContent?.[cmsKey]?.trim() ??
    memberIntakeContent?.[questionDisplayId]?.trim()
  if (cmsLabel) return cmsLabel
  return PHQ4_QUESTION_LABELS[questionDisplayId] ?? questionDisplayId
}

export const formatPhq4AnswerDisplay = (
  answer: MemberIntakePhq4Answer,
): string => answer.answerLabel?.trim() || ""

export const formatPresentingProblemPartPlainText = (
  part: MemberIntakePresentingProblemPart,
): string => {
  if (!part.display) return ""
  if (part.otherText) return `Other (${part.otherText})`
  return part.display
}

export const formatPresentingProblemPlainText = (
  presentingProblem: MemberIntakePresentingProblemDisplay,
): string => {
  const parts = [
    formatPresentingProblemPartPlainText(presentingProblem.primary),
    formatPresentingProblemPartPlainText(presentingProblem.secondary),
  ].filter((part) => part.length > 0)
  return parts.join(" ; ")
}
